import { DecimalPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { OrderReportDto, OrderReportHeaderDto } from '@core/dto';
import { statusAcronym } from '@core/enumerators';
import { IFilters, TimeClassification } from '@shared/interfaces/order';
import { Moment } from 'moment';
import { Subject } from 'rxjs';
import { OrderStatus, ReportGroupByEnum } from '../../../pages/analyze/enums';
import { PlantListInterface } from '../../../pages/record/interfaces';
import { PouringPerformanceService } from './pouring-performance.service';

@Injectable({
  providedIn: 'root',
})
export class OrdersActionsService {
  public detectChanges = new Subject<void>();

  public confirmed: OrderReportDto[];
  public assigned: OrderReportDto[];
  public inProcess: OrderReportDto[];
  public completed: OrderReportDto[];
  public cancelled: OrderReportDto[];
  public toBeConfirmed: OrderReportDto[];
  public onHold: OrderReportDto[];
  public grouped: OrderReportDto[] = [];
  public filters: IFilters | null = null;
  public response: OrderReportDto[] = [];
  public plants: PlantListInterface[] = [];
  public update: Moment = null;
  private header: OrderReportHeaderDto | null;
  public initialHeader: OrderReportHeaderDto | null = null;
  public classifiedOrders: Map<ReportGroupByEnum, OrderReportDto[]> = new Map<ReportGroupByEnum, OrderReportDto[]>();
  public fromDetail: boolean;
  public lastUpdate: Moment;

  constructor(
    private decimal: DecimalPipe,
    private pouring: PouringPerformanceService,
  ) {
    this.filters = {
      response: [],
      selectedRow: null,
      groupId: 0,
      time: TimeClassification.currentDay,
      search: '',
      selectedStatus: [],
      groupBy: ReportGroupByEnum.customer,
      dateRange: {
        from: new Date(),
        to: new Date(),
      },
      subGroupId: 0,
      plants: [],
      fieldToOrder: '',
      fieldTypeToOrder: '',
      fieldSortAsc: false,
      ordersOrderSort: '',
      ordersCustomerSort: '',
      ordersJobsiteSort: '',
      ordersPointOfDeliverySort: '',
      ordersProductShortDescSort: '',
      ordersConfirmQuantitySort: '',
      ordersDeliveryDateTimeSort: '',
      orderCode: '',
    };
  }

  public get getClassification(): string {
    const { groupBy } = this.filters;
    if (groupBy === ReportGroupByEnum.order) {
      return 'orderCode';
    } else if (groupBy === ReportGroupByEnum.jobSite) {
      return 'jobsite';
    } else if (groupBy === ReportGroupByEnum.customer) {
      return 'customerCode';
    } else {
      return 'pointOfDeliveryCode';
    }
  }

  groupOrdersBy(): void {
    this.grouped = [];
    const unique: any[] = [];

    const orders = this.getOrdersFilteredByStatus();

    const classification = this.getClassification;

    orders.forEach((row: any) => {
      // From all response we chose the 1st element to show on table
      if (
        !unique.find((order) => order[classification] === row[classification]) &&
        this.checkExtraProduct(row, classification) &&
        this.checkPumping(row, classification)
      ) {
        unique.push({ ...row, isVisible: true });
      }
    });
    this.grouped = unique;
  }

  /**
   * Groups orders and returns a deep copy of the grouped result.
   * Replaces the former GroupOrdersPipe.transform() logic.
   */
  groupOrders(allElements: OrderReportDto[]): OrderReportDto[] {
    if (!allElements) {
      return [];
    }
    this.groupOrdersBy();
    return JSON.parse(JSON.stringify(this.grouped));
  }

  /**
   * [If status selected on the screen then returns elements according to
   * status list]
   * @return {[OrderReportDto[]]}      [All elements that contains the status
   *   selected or all elements if no status selected]
   */
  getOrdersFilteredByStatus(): OrderReportDto[] {
    let status: OrderReportDto[] = [];
    if (this.filters.selectedStatus && this.filters.selectedStatus.length) {
      // Call to confirm handle as status true shows | false hide
      this.filters.selectedStatus.forEach((element) => {
        const ctco = element.code === 'CTCO';
        status = status.concat(
          this.response.filter(({ orderStatusCode, callToConfirm, extraProduct, pumpService }) =>
            ctco
              ? orderStatusCode === 'CONF' && !!callToConfirm // only confirmed orders and call to confirm
              : orderStatusCode === element.code && !extraProduct && !pumpService,
          ),
        );
      });
    } else {
      return this.response;
    }
    return status;
  }

  checkExtraProduct(row: any, option: string): boolean {
    let rest: OrderReportDto[];
    rest = this.response.filter((order: any) => order[option] === row[option]);
    rest = this.statusCheck(row, rest);
    return rest.every(({ extraProduct }) => extraProduct) || !row.extraProduct;
  }

  checkPumping(row: any, option: string): boolean {
    let rest: OrderReportDto[];
    rest = this.response.filter((order: any) => order[option] === row[option]);
    rest = this.statusCheck(row, rest);
    return rest.every(({ pumpService }) => pumpService) || !row.pumpService;
  }

  statusCheck(row: OrderReportDto, rest: OrderReportDto[]): OrderReportDto[] {
    // First filter array if status selected is valid
    let finalRows: OrderReportDto[] = [];
    const status = this.filters.selectedStatus ? this.filters.selectedStatus : [];
    if (!status.length) {
      finalRows = rest;
    } else {
      status.forEach(({ code }) => {
        if (code === 'IN-PROCESS') {
          finalRows = finalRows.concat(
            rest.filter(({ orderItemStatusCode }) => this.inProcessStatusValidation(orderItemStatusCode, code)),
          );
        } else {
          finalRows = finalRows.concat(rest.filter(({ orderItemStatusCode }) => orderItemStatusCode === code));
        }
      });
    }
    return finalRows;
  }

  public inProcessStatusValidation(orderItemStatusCode: string, selectedStatus: string): boolean {
    return (
      orderItemStatusCode === selectedStatus ||
      orderItemStatusCode === OrderStatus.loadingStart ||
      orderItemStatusCode === OrderStatus.toJobSite ||
      orderItemStatusCode === OrderStatus.onJobSite ||
      orderItemStatusCode === OrderStatus.unloading
    );
  }

  calculateValues(row: any, classification: string): any {
    const reducer = (accumulator: any, currentValue: any) => accumulator + currentValue;

    let rest: OrderReportDto[];
    if (row.isVisible) {
      rest = this.response.filter((order: any) => order[classification] === row[classification]);
    } else {
      rest = [row];
    }
    if (!rest.length) {
      return;
    }

    const loads = rest.filter(({ extraProduct, pumpService }) => !extraProduct && !pumpService);

    row.loads = loads.length;
    row.loadList = [...loads];
    const filteredOrders = [
      ...new Set(
        rest.filter(({ extraProduct, pumpService }) => !extraProduct && !pumpService).map(({ orderCode }) => orderCode),
      ),
    ];
    row.orders = filteredOrders.length;

    const orderList: OrderReportDto[] = [];
    filteredOrders.forEach((code) => {
      const o = rest.find(({ orderCode }) => orderCode === code);
      if (!orderList.find(({ orderCode }) => orderCode === o.orderCode)) {
        orderList.push(o);
      }
    });
    row.orderList = orderList;

    this.calculatePercentageByProperty('loading', row, rest);
    this.calculatePercentageByProperty('delivery', row, rest);
    this.calculatePercentageByProperty('optimal', row, rest);

    const filtered = rest.filter(({ extraProduct, pumpService }) => !extraProduct && !pumpService);
    if (filtered.length) {
      row.confirmQuantity = filtered.map(({ confirmQuantity }) => confirmQuantity).reduce(reducer);
    } else {
      row.confirmQuantity = 0;
    }

    const isPumping = rest.every(({ pumpService }) => pumpService);
    row.allLoadsArePumping = isPumping;

    row.enablePumpingIcon = rest.some(({ pumpingNeeded }) => pumpingNeeded);

    const pumpingServices = rest.filter(({ pumpService }) => pumpService);
    row.pumpingTooltip = '';
    pumpingServices.forEach((service) => {
      row.pumpingTooltip += `${service.productShortDesc ? service.productShortDesc : ''} - ${service.confirmQuantity} ${service.unitCode}<br/>`;
    });
    if (isPumping) {
      row.pumpingVolume = pumpingServices.map(({ confirmQuantity }) => confirmQuantity).reduce(reducer);
    }
    this.calculatePouringPerformance(loads, row);

    // extraProducts

    row.showExtraProducts = rest.some(({ extraProduct }) => extraProduct);

    row.showExtraProductsDesc = rest.every(({ extraProduct }) => extraProduct);

    const extraProducts = rest.filter(({ extraProduct }) => extraProduct);
    row.toolTipExtraProducts = '';
    extraProducts.forEach((extra) => {
      row.toolTipExtraProducts += `${extra.productShortDesc ? extra.productShortDesc : ''} - ${extra.confirmQuantity} ${extra.unitCode}<br/>`;
    });

    row.isExtraProduct = rest.every(({ extraProduct }) => extraProduct);
    return row;
  }

  calculatePercentageByProperty(option: string, row: OrderReportDto, loads: OrderReportDto[]): number {
    let dividend = 0;
    let divider = 0;
    const shippingFlag = 'SC';
    const statusValidation = (status: string) => {
      return (
        status === OrderStatus.loadingStart ||
        status === OrderStatus.toJobSite ||
        status === OrderStatus.onJobSite ||
        status === OrderStatus.unloading ||
        status === statusAcronym.completed
      );
    };

    switch (option) {
      case 'loading':
        // cmpl
        dividend = loads.filter(
          ({ loadingPunctualityFlag, orderItemStatusCode, extraProduct }) =>
            !extraProduct && loadingPunctualityFlag && statusValidation(orderItemStatusCode),
        ).length;
        divider = loads.filter(
          ({ loadingStartRealDateTime, orderItemStatusCode, extraProduct }) =>
            statusValidation(orderItemStatusCode) && !extraProduct,
        ).length;
        if (row) {
          row.calculatedLoading = loads.some(
            ({ loadingStartRealDateTime, orderItemStatusCode, extraProduct }) =>
              statusValidation(orderItemStatusCode) && !extraProduct,
          );
        }
        break;
      case 'delivery':
        dividend = loads.filter(
          ({ deliveryPunctualityFlag, orderItemStatusCode, extraProduct, shippingConditions }) =>
            !extraProduct &&
            deliveryPunctualityFlag &&
            statusValidation(orderItemStatusCode) &&
            shippingConditions !== shippingFlag,
        ).length;

        divider = loads.filter(
          ({ onJobsiteRealDateTime, orderItemStatusCode, extraProduct, shippingConditions }) =>
            statusValidation(orderItemStatusCode) && !extraProduct && shippingConditions !== shippingFlag,
        ).length;
        if (row) {
          row.calculatedDelivery = loads.some(
            ({ onJobsiteRealDateTime, orderItemStatusCode, extraProduct, shippingConditions }) =>
              statusValidation(orderItemStatusCode) && !extraProduct && shippingConditions !== shippingFlag,
          );
        }
        break;
      case 'optimal':
        dividend = loads.filter(
          ({ optimalPlantFlag, orderItemStatusCode, extraProduct, loadingStartRealDateTime }) =>
            !extraProduct && optimalPlantFlag && statusValidation(orderItemStatusCode),
        ).length;
        divider = loads.filter(
          ({ orderItemStatusCode, extraProduct }) => statusValidation(orderItemStatusCode) && !extraProduct,
        ).length;
        if (row) {
          row.calculatedOptimal = loads.some(
            ({ orderItemStatusCode, extraProduct }) => statusValidation(orderItemStatusCode) && !extraProduct,
          );
        }
        break;
    }
    let finalValue = 0;
    if (dividend === 0 || divider === 0) {
      finalValue = 0;
    } else {
      finalValue = Number(this.decimal.transform((dividend / divider) * 100, '1.1-2'));
    }
    if (option === 'loading' && row) {
      row.loadingOnTime = finalValue;
    } else if (option === 'delivery' && row) {
      row.deliveryOnTime = finalValue;
    } else if (option === 'optimal' && row) {
      row.optimalPlant = finalValue;
    }
    return finalValue;
  }

  /*
   * [Calculate pouring performance time planned, pouring time real and pouring performance]
   * @param  {[OrderReportDto[]]} loads [loads from that belongs to row parameter]
   * @param  {[OrderReportDto]} row [order header]
   * @return {[number]}      [Returns pouring performance]
   */
  calculatePouringPerformance(loads: OrderReportDto[], row: OrderReportDto): number {
    let pouringTimePlanned = 0;
    let pouringTimeReal = 0;
    let performance = 0;
    // reset values
    row.pouringColorValue = 0;
    row.pouringPerformance = 0;

    [...new Set(loads.map(({ orderCode }) => orderCode))].forEach((element) => {
      const allByOrder = loads.filter(
        ({ orderCode, orderItemStatusCode }) => orderCode === element && orderItemStatusCode !== OrderStatus.cancelled,
      );

      if (allByOrder.length <= 1 && row) {
        row.pouringPerformance = 0;
        return;
      }
      const {
        pouringTimePlanned: ptp,
        pouringTimeReal: ptr,
        performance: perf,
      } = this.pouring.getPouringTimePlannedAndPouringTimeReal(this.update, allByOrder);
      pouringTimePlanned += ptp;
      pouringTimeReal += ptr;
      performance += perf;
    });
    if (row) {
      row.pouringColorValue = performance;
      row.plannedTime = pouringTimePlanned;
      row.pouringPerformance = pouringTimeReal;
    }
    return performance;
  }

  public isSelectedStatus(status: string): boolean {
    if (!this.filters.selectedStatus) {
      return false;
    }
    const statusList = this.filters.selectedStatus.map(({ code }) => code);
    return statusList.includes(status);
  }

  fillHeaderValues(): OrderReportHeaderDto {
    let status: OrderReportDto[] = [];
    let includesCTCO = false;
    if (this.filters.selectedStatus && this.filters.selectedStatus.length) {
      includesCTCO = this.filters.selectedStatus.some(({ code }) => code === 'CTCO');
      this.filters.selectedStatus.forEach((element) => {
        const ctco = element.code === 'CTCO';
        status = status.concat(
          this.response.filter(({ orderStatusCode, callToConfirm }) =>
            ctco
              ? orderStatusCode === 'CONF' && !!callToConfirm // only confirmed orders and call to confirm
              : orderStatusCode === element.code,
          ),
        );
      });
    } else {
      status = this.response;
    }

    if (includesCTCO) {
      const classification = this.getClassification;
      status.forEach((order: any) => {
        if (order.callToConfirm) {
          status = status.concat(this.response.filter((o: any) => o[classification] === order[classification]));
        }
      });
      status = this.deleteRepeatedOrders(status);
    }
    this.classifyResults(status);

    this.header = {
      loadingOnTime: this.toShortDecimal(this.calculatePercentageByProperty('loading', null, status)),
      deliveryOnTime: this.toShortDecimal(this.calculatePercentageByProperty('delivery', null, status)),
      optimalPlant: this.toShortDecimal(this.calculatePercentageByProperty('optimal', null, status)),
      //
      orderLoads: status.filter((f) => !f.extraProduct && !f.pumpService).length,
      orderVolume: this.toShortDecimal(this.getTotalVolume(status)),
      totalOrders: this.toShortDecimal(this.getTotalOrders(status)),
      unitCode: status[0]?.unitCode,
      //
      confirmed: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.confirmed)),
      assigned: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.assigned)),
      inProcess: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.loadingStart)),
      completed: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.completed)),
      cancelled: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.cancelled)),
      toBeConfirmed: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.toBeConfirmed)),
      onHold: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.onHold)),
    };
    return this.header;
  }

  deleteRepeatedOrders(list: OrderReportDto[]): OrderReportDto[] {
    const uniqueElements: OrderReportDto[] = [];
    list.forEach((order) => {
      if (!uniqueElements.find((el) => el.orderItemId === order.orderItemId)) {
        uniqueElements.push(order);
      }
    });
    return uniqueElements;
  }

  public classifyResults(arraySource: OrderReportDto[]): void {
    this.confirmed = arraySource.filter(
      ({ orderItemStatusCode, extraProduct, pumpService }) =>
        orderItemStatusCode === OrderStatus.confirmed && !extraProduct && !pumpService,
    );
    this.assigned = arraySource.filter(
      ({ orderItemStatusCode, extraProduct, pumpService }) =>
        orderItemStatusCode === OrderStatus.assigned && !extraProduct && !pumpService,
    );
    this.completed = arraySource.filter(
      ({ orderItemStatusCode, extraProduct, pumpService }) =>
        orderItemStatusCode === OrderStatus.completed && !extraProduct && !pumpService,
    );
    this.cancelled = arraySource.filter(
      ({ orderItemStatusCode, extraProduct, pumpService }) =>
        orderItemStatusCode === OrderStatus.cancelled && !extraProduct && !pumpService,
    );
    this.toBeConfirmed = arraySource.filter(
      ({ orderItemStatusCode, extraProduct, pumpService }) =>
        orderItemStatusCode === OrderStatus.toBeConfirmed && !extraProduct && !pumpService,
    );
    this.onHold = arraySource.filter(
      ({ orderItemStatusCode, extraProduct, pumpService }) =>
        orderItemStatusCode === OrderStatus.onHold && !extraProduct && !pumpService,
    );
    this.inProcess = arraySource.filter(
      ({ orderItemStatusCode, extraProduct, pumpService }) =>
        (orderItemStatusCode === OrderStatus.loadingStart ||
          orderItemStatusCode === OrderStatus.toJobSite ||
          orderItemStatusCode === OrderStatus.onJobSite ||
          orderItemStatusCode === OrderStatus.unloading) &&
        !extraProduct &&
        !pumpService,
    );
  }

  getTotalVolume(array: OrderReportDto[]): number {
    const reducer = (accumulator: number, currentValue: number) => accumulator + currentValue;

    const vol = array
      .filter((order) => {
        return !order.extraProduct && !order.pumpService;
      })
      .map(({ confirmQuantity }) => Number(confirmQuantity.toString().replace(',', '')));
    if (!vol.length) {
      return 0;
    }
    return vol.reduce(reducer);
  }

  getTotalOrders(array: OrderReportDto[]): number {
    const unique: any[] = [];
    if (this.filters) {
      array.forEach(({ orderCode }) => {
        if (!unique.includes(orderCode)) {
          unique.push(orderCode);
        }
      });
    }

    return unique.length;
  }

  getVolumeByStatus(option: OrderStatus): number {
    const reducer = (accumulator: number, currentValue: number) => accumulator + currentValue;
    let volumeList: number[] = [];
    switch (option) {
      // In process case

      case OrderStatus.loadingStart:
      case OrderStatus.toJobSite:
      case OrderStatus.onJobSite:
      case OrderStatus.unloading:
        volumeList = this.inProcess.map(({ confirmQuantity }) => confirmQuantity); // ok
        break;
      case OrderStatus.confirmed:
        volumeList = this.confirmed.map(({ confirmQuantity }) => confirmQuantity); // ok
        break;
      case OrderStatus.assigned:
        volumeList = this.assigned.map(({ confirmQuantity }) => confirmQuantity);
        break;
      case OrderStatus.completed:
        volumeList = this.completed.map(({ confirmQuantity }) => confirmQuantity); // ok
        break;
      case OrderStatus.cancelled:
        volumeList = this.cancelled.map(({ confirmQuantity }) => confirmQuantity); // ok
        break;
      case OrderStatus.toBeConfirmed:
        volumeList = this.toBeConfirmed.map(({ confirmQuantity }) => confirmQuantity);
        break;
      case OrderStatus.onHold:
        volumeList = this.onHold.map(({ confirmQuantity }) => confirmQuantity);
        break;
    }
    return volumeList.length > 0 ? volumeList.reduce(reducer) : 0;
  }

  fillHeaderWithRowSelected(): OrderReportHeaderDto {
    if (this.filters.selectedRow) {
      const order: any = this.filters.selectedRow;
      if (order.isVisible) {
        const classification = this.getClassification;
        const results = this.response.filter(
          (o: any) => o[classification] === order[classification] && !o.extraProduct && !o.pumpService,
        );
        this.classifyResults(results);
      } else {
        this.classifyResults([order]);
        order.orders = 1;
        order.loads = 1;
        order.loadingOnTime =
          order.loadingPunctualityFlag && order.loadingStartRealDateTime !== '0001-01-01T00:00:00' ? 100 : 0;
        order.deliveryOnTime =
          order.deliveryPunctualityFlag && order.onJobsiteRealDateTime !== '0001-01-01T00:00:00' ? 100 : 0;
        order.optimalPlant =
          order.optimalPlantFlag && order.loadingStartRealDateTime !== '0001-01-01T00:00:00' ? 100 : 0;
      }

      this.header = {
        loadingOnTime: this.toShortDecimal(order.loadingOnTime),
        deliveryOnTime: this.toShortDecimal(order.deliveryOnTime),
        optimalPlant: this.toShortDecimal(order.optimalPlant),
        //
        orderLoads: this.toShortDecimal(order.loads),
        orderVolume: this.toShortDecimal(order.confirmQuantity),
        totalOrders: this.toShortDecimal(order.orders),
        unitCode: order.orders[0] ? order.orders[0]?.unitCode : order.unitCode,
        //
        confirmed: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.confirmed)),
        assigned: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.assigned)),
        inProcess: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.loadingStart)),
        completed: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.completed)),
        cancelled: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.cancelled)),
        toBeConfirmed: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.toBeConfirmed)),
        onHold: this.toShortDecimal(this.getVolumeByStatus(OrderStatus.onHold)),
      };

      return this.header;
    }
  }

  clearRow(): void {
    if (!this.filters.selectedStatus || !this.filters.selectedStatus.length) {
      this.classifyResults(this.response);
      this.fillHeaderValues();
    } else {
      // this.getGroupedBy(this.order-filters.groupBy);
      this.classifyResults(this.getOrdersFilteredByStatus());
      this.fillHeaderValues();
    }
  }

  toShortDecimal(value: number): number {
    return +this.decimal.transform(value, '1.0-2').replace(',', '');
  }

  /**
   * [This function will classify the orders list param in three ways taking
   * orderCode or jobsite or customerCode from every element]
   * @param  {[OrderReportDto[]]} orders [the orders to be classified]
   */
  async classifyOrders(orders: OrderReportDto[]): Promise<void> {
    const [order, jobSite, customer, pointOfDelivery] = await Promise.all([
      this.classifyBy(orders, ReportGroupByEnum.order),
      this.classifyBy(orders, ReportGroupByEnum.jobSite),
      this.classifyBy(orders, ReportGroupByEnum.customer),
      this.classifyBy(orders, ReportGroupByEnum.pointOfDelivery),
    ]);
    this.classifiedOrders.set(ReportGroupByEnum.order, order);
    this.classifiedOrders.set(ReportGroupByEnum.jobSite, jobSite);
    this.classifiedOrders.set(ReportGroupByEnum.customer, customer);
    this.classifiedOrders.set(ReportGroupByEnum.pointOfDelivery, pointOfDelivery);
  }

  classifyBy(orders: OrderReportDto[], key: ReportGroupByEnum): Promise<OrderReportDto[]> {
    return new Promise<OrderReportDto[]>((resolve, reject) => {
      const unique: OrderReportDto[] = [];
      orders.forEach((row: OrderReportDto) => {
        // From all response we chose the 1st element to show on table
        if (
          !unique.find((order: any) => order[key] === row[key]) &&
          this.checkExtraProduct(row, key) &&
          this.checkPumping(row, key)
        ) {
          unique.push({ ...row, isVisible: true });
        }
      });
      resolve(unique);
    });
  }

  public get getClassifiedOrders(): OrderReportDto[] {
    return this.classifiedOrders.get(this.filters.groupBy);
  }
}
